import { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';

import 'react-phone-input-2/lib/material.css';

const MX_COUNTRY_CODE = '52';
const MX_LOCAL_DIGITS = 10;
const MX_TOTAL_DIGITS = MX_COUNTRY_CODE.length + MX_LOCAL_DIGITS;
const MX_MASK = '.. .... ....';
const MX_INPUT_MAX_LENGTH = 16;
const MX_PLACEHOLDER = '+52 95 3278 5480';

const normalizePhoneDigits = (raw, { includeCountryCode = true, maxDigits = MX_TOTAL_DIGITS } = {}) => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';

  let normalized = digits;
  if (includeCountryCode && !normalized.startsWith(MX_COUNTRY_CODE) && normalized.length <= MX_LOCAL_DIGITS) {
    normalized = `${MX_COUNTRY_CODE}${normalized}`;
  }

  if (normalized.length > maxDigits) {
    normalized = normalized.slice(0, maxDigits);
  }

  return normalized;
};

const getLocalLength = (digits) => {
  if (!digits) return 0;
  if (digits.startsWith(MX_COUNTRY_CODE)) {
    return digits.length - MX_COUNTRY_CODE.length;
  }
  return digits.length;
};

function PhoneNumberControl(props) {
  const [lastLength, setLastLength] = useState(0);
  const [localError, setLocalError] = useState(false);

  const {
    disabled,
    display,
    events = {},
    error,
    label,
    name,
    onBlur,
    onChange,
    placeholder,
    settings = {},
    value,
    hideLabel,
  } = props;
  const labelStyle = {
    textTransform: 'none',
    letterSpacing: '0.08em',
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7A90',
    ...display?.labelStyle,
  };
  if (disabled) {
    labelStyle.color = '#0000004e';
  }
  const inputStyle = {
    borderRadius: '12px',
    height: '44px',
    paddingLeft: '52px',
    ...display?.inputStyle,
  };

  const resolvedPlaceholder = placeholder || MX_PLACEHOLDER;
  const maxDigits = settings?.maxDigits || MX_TOTAL_DIGITS;
  const maxInputLength = settings?.maxLength || MX_INPUT_MAX_LENGTH;
  const onlyCountries = settings?.onlyCountries || ['mx'];
  const normalizedValue = normalizePhoneDigits(value, {
    includeCountryCode: true,
    maxDigits,
  });

  useEffect(() => {
    setLastLength(getLocalLength(normalizedValue));
  }, [normalizedValue]);

  const handleChange = function (phone) {
    const normalizedPhone = normalizePhoneDigits(phone, {
      includeCountryCode: true,
      maxDigits,
    });
    setLastLength(getLocalLength(normalizedPhone));

    const { prefix = '+' } = settings;
    const target = {
      name,
      value: `${prefix}${normalizedPhone}`,
    };
    onChange({
      target,
    });

    if (events?.onChange) {
      events.onChange(target);
    }
  };

  const handleBlur = () => {
    if (lastLength !== 0 && lastLength !== MX_LOCAL_DIGITS) {
      setLocalError(true);
    } else {
      setLocalError(false);
    }
    if (onBlur) {
      const { prefix = '+' } = settings;
      const blurEvent = {
        target: {
          name,
          value: `${prefix}${normalizedValue}`,
        },
        persist: () => { },
      };
      onBlur(blurEvent);
    }
  };

  useEffect(() => {
    const el = document.querySelector('.special-label');
    if (el) {
      el.style.display = 'none';
    }
  }, [disabled]);

  return (
    <>
      <Box
        sx={{
          mb: 2,
        }}
      >
        {!hideLabel && (
          <Typography
            style={{
              ...labelStyle,
            }}
            variant="h6"
            gutterBottom
          >
            {label}
          </Typography>
        )}
        <PhoneInput
          country={settings?.defaultCountry || 'mx'}
          disabled={disabled}
          disableDropdown={true}
          countryCodeEditable={false}
          masks={{
            mx: MX_MASK,
          }}
          onlyCountries={onlyCountries}
          inputProps={{
            style: {
              cursor: disabled ? 'default' : undefined,
              width: '100%',
              ...inputStyle,
            },
            maxLength: maxInputLength,
          }}
          isValid={() => !!!error && !localError}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={resolvedPlaceholder}
          prefix={settings?.prefix || '+'}
          specialLabel={hideLabel ? '' : label}
          value={normalizedValue}
        />
      </Box>
      {(localError || props.helperText) && (
        <Box mt={-1.5}>
          <Typography
            color="error"
            children={
              localError
                ? 'El número de teléfono debe tener al menos 10 dígitos.'
                : props.helperText
            }
            variant="caption"
            style={{
              marginLeft: '14px',
            }}
          />
        </Box>
      )}
    </>
  );
}

PhoneNumberControl.defaultProps = {
  disabled: false,
  error: false,
  onChange: () => { },
  onBlur: () => { },
  hideLabel: false,
};

PhoneNumberControl.propTypes = {
  disabled: PropTypes.bool,
  display: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  events: PropTypes.shape({
    onChange: PropTypes.func,
  }),
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  hideLabel: PropTypes.bool,
  settings: {
    defaultCountry: PropTypes.string,
    disableDropdown: PropTypes.bool,
    onlyCountries: PropTypes.arrayOf(PropTypes.string),
    prefix: PropTypes.string,
  },
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PhoneNumberControl;
